import React from 'react';
import {Modal} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {Keyboard, Question, X} from '../../../icons';
import {LINE_DEFINITION_KEY} from '../../panel_v11/lineDefinition';

/**
 * La ayuda del Recorrido: «¿Qué es cada cosa?» y «Atajos».
 *
 * Dos overlays chicos que el mockup pone en la barra superior y que acá viven arriba del hilo, al
 * alcance desde cualquier acto. El glosario son siete palabras con una línea cada una: es lo que
 * hace falta para usar la pantalla entera sin que nadie la explique. Los atajos son los del mazo
 * de Revisa, que hoy sólo se descubren leyendo la línea gris debajo de las cartas — y el `?`, que
 * es el atajo que abre este panel, no se descubre de ninguna forma.
 *
 * Es UN modal con dos modos y no dos modales: se puede saltar de uno al otro sin cerrar, y la
 * lógica de foco/Escape/`modal-open` la pone reactstrap una sola vez.
 */

export const HELP_GLOSSARY = 'glossary';
export const HELP_SHORTCUTS = 'shortcuts';

// Los términos, en el orden en que aparecen en el Recorrido: de lo que entra a lo que se usa. El
// modificador pinta el punto con el color con el que ese objeto se ve en la pantalla.
// La línea de seguimiento va AL FINAL (card mFHzKeNK): no es un objeto del Recorrido sino el que
// le da criterio a la IA, y su texto es la definición única del producto (`textKey`), no uno propio.
export const GLOSSARY_TERMS = [
    {key: 'fact', tone: '-ia'},
    {key: 'pending', tone: '-warn'},
    {key: 'noise', tone: '-dim'},
    {key: 'note', tone: '-note'},
    {key: 'material', tone: '-material'},
    {key: 'history', tone: '-history'},
    {key: 'line', tone: '-ok', textKey: LINE_DEFINITION_KEY},
];

// Las teclas del mazo, tal como las escucha `BoosterReviewDeck`. Si allá cambia una, cambia acá:
// prometer un atajo que no existe es peor que no listarlo.
export const SHORTCUTS = [
    {key: 'approve', keys: ['→']},
    {key: 'reject', keys: ['←']},
    {key: 'edit', keys: ['E']},
    {key: 'undo', keys: ['Ctrl/⌘', 'Z']},
    {key: 'help', keys: ['?']},
    {key: 'close', keys: ['Esc']},
];

/** El foco está en algo que escribe: `?` ahí es un signo de pregunta, no un atajo. */
function isTypingTarget(el) {
    if (!el) return false;
    const tag = (el.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return el.isContentEditable === true;
}

/**
 * ¿Este `keydown` pide el panel de atajos? Mismas guardas que el teclado del mazo: no mientras se
 * escribe, no con otro modal abierto (reactstrap marca `body.modal-open`).
 */
export function isHelpShortcut(event, doc = document) {
    if (!event || event.key !== '?') return false;
    if (isTypingTarget(doc.activeElement)) return false;
    if (doc.body && doc.body.classList.contains('modal-open')) return false;
    return true;
}

export function BoosterHelpButtons({onOpen, showShortcuts = true}) {
    const {t} = useTranslation();
    return (
        <div className="iab-help" role="group" aria-label={t('ia_booster.help.aria')}>
            <button type="button" className="iab-help-btn" onClick={() => onOpen(HELP_GLOSSARY)}>
                <Question size={14} weight="bold" aria-hidden="true"/>
                {t('ia_booster.help.glossary_btn')}
            </button>
            {showShortcuts && (
                <button type="button" className="iab-help-btn" onClick={() => onOpen(HELP_SHORTCUTS)}>
                    <Keyboard size={14} weight="bold" aria-hidden="true"/>
                    {t('ia_booster.help.shortcuts_btn')}
                </button>
            )}
        </div>
    );
}

export function BoosterHelpModal({mode, onClose, onModeChange, showShortcuts = true}) {
    const {t} = useTranslation();
    const isOpen = mode === HELP_GLOSSARY || mode === HELP_SHORTCUTS;
    const active = mode === HELP_SHORTCUTS ? HELP_SHORTCUTS : HELP_GLOSSARY;
    const tabClass = (which) => `iab-modal__tab ${active === which ? '-on' : ''}`;

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            modalClassName="iab-modal"
            contentClassName="iab-modal__content"
            unmountOnClose
            aria-labelledby="iab-help-title"
        >
            <div className="iab-modal__head">
                {showShortcuts ? (
                    <div className="iab-modal__tabs" role="tablist">
                        <button type="button" role="tab" className={tabClass(HELP_GLOSSARY)}
                                aria-selected={active === HELP_GLOSSARY}
                                onClick={() => onModeChange && onModeChange(HELP_GLOSSARY)}>
                            {t('ia_booster.help.glossary_title')}
                        </button>
                        <button type="button" role="tab" className={tabClass(HELP_SHORTCUTS)}
                                aria-selected={active === HELP_SHORTCUTS}
                                onClick={() => onModeChange && onModeChange(HELP_SHORTCUTS)}>
                            {t('ia_booster.help.shortcuts_title')}
                        </button>
                    </div>
                ) : <span/>}
                <button type="button" className="iab-modal__close" onClick={onClose}
                        aria-label={t('ia_booster.help.close')}>
                    <X size={14} weight="bold"/>
                </button>
            </div>

            {active === HELP_GLOSSARY ? (
                <div className="iab-modal__body">
                    <h3 className="iab-modal__title" id="iab-help-title">
                        {t('ia_booster.help.glossary_title')}
                    </h3>
                    <p className="iab-modal__sub">{t('ia_booster.help.glossary_sub')}</p>
                    <dl className="iab-terms">
                        {GLOSSARY_TERMS.map(term => (
                            <div key={term.key} className="iab-term">
                                <span className={`iab-term__dot ${term.tone}`} aria-hidden="true"/>
                                <div className="iab-term__body">
                                    <dt>{t(`ia_booster.help.term_${term.key}`)}</dt>
                                    <dd>{t(term.textKey || `ia_booster.help.term_${term.key}_text`)}</dd>
                                </div>
                            </div>
                        ))}
                    </dl>
                </div>
            ) : (
                <div className="iab-modal__body">
                    <h3 className="iab-modal__title" id="iab-help-title">
                        {t('ia_booster.help.shortcuts_title')}
                    </h3>
                    <p className="iab-modal__sub">{t('ia_booster.help.shortcuts_sub')}</p>
                    <ul className="iab-kbdlist">
                        {SHORTCUTS.map(item => (
                            <li key={item.key} className="iab-kbdrow">
                                <span>{t(`ia_booster.help.key_${item.key}`)}</span>
                                <span className="iab-kbdkeys">
                                    {item.keys.map(k => <kbd key={k} className="iab-kbd">{k}</kbd>)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Modal>
    );
}

export default BoosterHelpModal;
